import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RiPlayFill, RiImage2Line } from 'react-icons/ri';
import './Gallery.css';
import LanguageContext from '../../context/LanguageContext.js';
import { useStorePath } from '../../lib/storefront/storePath.js';
import { fetchGalleryPhotos } from '../../api/galleryApi.js';
import { getGalleryPhotosPlaceholder } from '../../data/galleryPhotosPlaceholder.js';
import { resolveMediaUrl } from '../../utils/mediaUrl.js';
import { computeJustifiedRows, targetRowHeight } from './justifiedLayout.js';
import StoryViewer from './StoryViewer.jsx';

const ALL = '__all__';
const INITIAL_COUNT = 24;
const PAGE_SIZE = 24;
const FALLBACK_RATIO = 4 / 3;
const RATIO_TIMEOUT_MS = 4000;

function toPhoto(item, index) {
    const src = resolveMediaUrl(item?.imgUrl) || item?.imgUrl;
    if (!src) return null;
    return {
        id: String(item._id || item.id || `${index}-${src}`),
        src,
        description: String(item.description || '').trim(),
        category: String(item.category || '').trim(),
    };
}

/** Loads each photo's natural aspect ratio (with a timeout fallback) so rows can be justified. */
function useAspectRatios(photos) {
    const [ratios, setRatios] = useState({});

    useEffect(() => {
        let cancelled = false;
        const pending = photos.filter((photo) => ratios[photo.id] === undefined);
        if (pending.length === 0) return undefined;

        const found = {};
        let flushTimer = null;
        const flush = () => {
            flushTimer = null;
            if (cancelled || Object.keys(found).length === 0) return;
            const batch = { ...found };
            Object.keys(found).forEach((key) => delete found[key]);
            setRatios((current) => ({ ...current, ...batch }));
        };
        const record = (id, ratio) => {
            found[id] = ratio;
            if (!flushTimer) flushTimer = setTimeout(flush, 60);
        };

        pending.forEach((photo) => {
            const img = new Image();
            img.onload = () => record(photo.id, img.naturalWidth / img.naturalHeight || FALLBACK_RATIO);
            img.onerror = () => record(photo.id, FALLBACK_RATIO);
            img.src = photo.src;
        });

        // Never hold the page hostage to one slow image.
        const giveUp = setTimeout(() => {
            if (cancelled) return;
            setRatios((current) => {
                const next = { ...current };
                pending.forEach((photo) => {
                    if (next[photo.id] === undefined) next[photo.id] = FALLBACK_RATIO;
                });
                return next;
            });
        }, RATIO_TIMEOUT_MS);

        return () => {
            cancelled = true;
            clearTimeout(giveUp);
            if (flushTimer) clearTimeout(flushTimer);
        };
        // Only re-run when the set of photos changes; `ratios` is read for the pending check.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [photos]);

    return ratios;
}

function useElementWidth(ref) {
    const [width, setWidth] = useState(0);
    useLayoutEffect(() => {
        const node = ref.current;
        if (!node) return undefined;
        setWidth(node.clientWidth);
        const observer = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
        observer.observe(node);
        return () => observer.disconnect();
    }, [ref]);
    return width;
}

function JustifiedTile({ photo, width, height, onOpen, tileRef, index }) {
    const { t } = useTranslation();
    const [loaded, setLoaded] = useState(false);

    return (
        <button
            ref={tileRef}
            type="button"
            className={`eg-tile${loaded ? ' eg-tile--loaded' : ''}`}
            style={{ width: `${width}px`, height: `${height}px`, '--eg-delay': `${Math.min(index, 12) * 45}ms` }}
            onClick={() => onOpen(index)}
            aria-label={photo.description ? t('gallery.openPhotoNamed', { name: photo.description }) : t('gallery.photoIndex', { n: index + 1 })}
        >
            <img
                src={photo.src}
                alt=""
                loading={index < 8 ? 'eager' : 'lazy'}
                decoding="async"
                className="eg-tile-img"
                onLoad={() => setLoaded(true)}
                onError={() => setLoaded(true)}
                draggable={false}
            />
            {photo.description && (
                <span className="eg-tile-caption" aria-hidden="true">
                    <span>{photo.description}</span>
                </span>
            )}
        </button>
    );
}

function Gallery() {
    const { t, i18n } = useTranslation();
    const { dir } = useContext(LanguageContext);
    const storePath = useStorePath();
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState(ALL);
    const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);
    const [viewer, setViewer] = useState(null); // { index, autoplay }
    const gridRef = useRef(null);
    const tileRefs = useRef([]);
    const lastOpened = useRef(null);
    const width = useElementWidth(gridRef);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const data = await fetchGalleryPhotos();
                const remote = (Array.isArray(data) ? data : [])
                    .filter((item) => item?.status !== 'inactive')
                    .map(toPhoto)
                    .filter(Boolean);
                if (!cancelled) setPhotos(remote.length ? remote : getGalleryPhotosPlaceholder(t).map(toPhoto));
            } catch {
                if (!cancelled) setPhotos(getGalleryPhotosPlaceholder(t).map(toPhoto));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // Fetch once; demo captions re-translate via the effect below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Demo photos carry translated captions — refresh them on a language switch.
    useEffect(() => {
        setPhotos((current) =>
            current.length && current[0].id.startsWith('placeholder-')
                ? getGalleryPhotosPlaceholder(t).map(toPhoto)
                : current
        );
    }, [t]);

    const categories = useMemo(() => [...new Set(photos.map((photo) => photo.category).filter(Boolean))], [photos]);
    const filtered = useMemo(
        () => (category === ALL ? photos : photos.filter((photo) => photo.category === category)),
        [photos, category]
    );
    const visible = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
    const ratios = useAspectRatios(visible);

    // Lay out the longest run of photos whose shape is known; later ones join as they arrive,
    // so "Load more" appends rows instead of blanking the grid.
    const readyCount = useMemo(() => {
        const firstUnknown = visible.findIndex((photo) => ratios[photo.id] === undefined);
        return firstUnknown === -1 ? visible.length : firstUnknown;
    }, [visible, ratios]);

    const gap = width < 480 ? 6 : 10;
    const rows = useMemo(
        () =>
            computeJustifiedRows(
                visible.slice(0, readyCount).map((photo) => ratios[photo.id]),
                width,
                targetRowHeight(width),
                gap
            ),
        [visible, readyCount, ratios, width, gap]
    );
    const showSkeleton = loading || !width || readyCount === 0;

    const cover = photos[0];
    const remaining = filtered.length - visible.length;

    const openViewer = useCallback((index, autoplay = false) => {
        lastOpened.current = index;
        setViewer({ index, autoplay });
    }, []);

    const changeViewer = useCallback((index) => {
        lastOpened.current = index;
        setViewer((current) => (current ? { ...current, index } : current));
        setVisibleCount((count) => {
            let next = count;
            while (index >= next) next += PAGE_SIZE;
            return next;
        });
    }, []);

    const closeViewer = useCallback(() => {
        setViewer(null);
        const index = lastOpened.current;
        // After the viewer unmounts. (Not requestAnimationFrame: it is paused in background tabs.)
        setTimeout(() => tileRefs.current[index]?.focus({ preventScroll: true }), 0);
    }, []);

    const chooseCategory = (key) => {
        setCategory(key);
        setVisibleCount(INITIAL_COUNT);
    };

    return (
        <main className="main eg-page" dir={dir()}>
            {/* ---- Cinematic cover ---- */}
            <header className={`eg-cover${loading ? ' eg-cover--loading' : ''}`}>
                {!loading && cover && (
                    <img src={cover.src} alt="" className="eg-cover-img" aria-hidden="true" />
                )}
                <div className="eg-cover-shade" aria-hidden="true" />
                <div className="container eg-cover-inner">
                    <nav className="eg-crumbs" aria-label={t('gallery.breadcrumbLabel')}>
                        <Link to={storePath(`/${i18n.language}`)}>{t('breadcrumb.home', 'Home')}</Link>
                        <span aria-hidden="true">/</span>
                        <span aria-current="page">{t('breadcrumb.gallery')}</span>
                    </nav>
                    <span className="eg-eyebrow">
                        <RiImage2Line aria-hidden="true" />
                        {t('breadcrumb.gallery')}
                    </span>
                    <h1 className="eg-title">{t('gallery.title')}</h1>
                    <p className="eg-subtitle">{t('gallery.description')}</p>
                    <div className="eg-cover-actions">
                        {!loading && filtered.length > 1 && (
                            <button type="button" className="eg-play" onClick={() => openViewer(0, true)}>
                                <span className="eg-play-icon" aria-hidden="true"><RiPlayFill /></span>
                                {t('gallery.startSlideshow')}
                            </button>
                        )}
                        {!loading && photos.length > 0 && (
                            <span className="eg-count">{t('gallery.photoCount', { count: photos.length })}</span>
                        )}
                    </div>
                </div>
            </header>

            <section className="eg-body">
                <div className="container">
                    {!loading && categories.length > 1 && (
                        <div className="eg-chips" role="group" aria-label={t('gallery.categoriesNavAria')}>
                            {[ALL, ...categories].map((key) => {
                                const active = category === key;
                                const label = key === ALL ? t('gallery.all') : t(`gallery.categories.${key}`, { defaultValue: key });
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        className={`eg-chip${active ? ' eg-chip--active' : ''}`}
                                        aria-pressed={active}
                                        onClick={() => chooseCategory(key)}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    <div ref={gridRef} className="eg-grid" style={{ '--eg-gap': `${gap}px` }}>
                        {showSkeleton ? (
                            <div className="eg-skeleton" aria-busy="true" aria-label={t('gallery.loading')}>
                                {[[1.4, 0.8, 1.2], [0.9, 1.6, 1], [1.1, 1.3, 0.8]].map((row, r) => (
                                    <div key={r} className="eg-skeleton-row">
                                        {row.map((grow, i) => (
                                            <span key={i} className="eg-skeleton-tile" style={{ flexGrow: grow }} />
                                        ))}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            rows.map((row, r) => (
                                <div
                                    key={r}
                                    className={`eg-row${row.complete ? '' : ' eg-row--last'}`}
                                    style={{ height: `${row.height}px` }}
                                >
                                    {row.items.map(({ index, width: tileWidth }) => (
                                        <JustifiedTile
                                            key={visible[index].id}
                                            photo={visible[index]}
                                            index={index}
                                            width={tileWidth}
                                            height={row.height}
                                            onOpen={(photoIndex) => openViewer(photoIndex)}
                                            tileRef={(node) => {
                                                tileRefs.current[index] = node;
                                            }}
                                        />
                                    ))}
                                </div>
                            ))
                        )}
                        {!showSkeleton && readyCount < visible.length && (
                            <div className="eg-row eg-row--pending" aria-hidden="true">
                                <span className="eg-skeleton-tile" />
                                <span className="eg-skeleton-tile" />
                                <span className="eg-skeleton-tile" />
                            </div>
                        )}
                    </div>

                    {!loading && remaining > 0 && (
                        <div className="eg-more">
                            <button type="button" className="eg-more-btn" onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>
                                {t('gallery.loadMore', { count: Math.min(PAGE_SIZE, remaining) })}
                            </button>
                            <span className="eg-more-meta">
                                {t('gallery.showing', { shown: visible.length, total: filtered.length })}
                            </span>
                        </div>
                    )}
                </div>
            </section>

            {viewer && (
                <StoryViewer
                    photos={filtered}
                    index={viewer.index}
                    autoplay={viewer.autoplay}
                    onChange={changeViewer}
                    onClose={closeViewer}
                />
            )}
        </main>
    );
}

export default Gallery;
